import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useQueryParams, useTooltip } from '../../hooks';

import './MainColumn.css';

export const MainColumn = ({ children }) => {
  const { queryParams, setHistory } = useQueryParams();
  const { showTooltip, hideTooltip } = useTooltip();

  const navigate = useNavigate();

  const initialWidth = 480;

  const [mainColumnWidth, setMainColumnWidth] = useState(
    parseInt(localStorage.getItem('mainColumnWidth')) || initialWidth
  ); // initial width of the div
  const [isResizing, setIsResizing] = useState(false);
  const isResizingRef = useRef(false);
  const startXRef = useRef(0);
  const rawWidthRef = useRef(parseInt(localStorage.getItem('mainColumnWidth')) || initialWidth);
  const [hideMainColumn, setHideMainColumn] = useState(
    queryParams.hidedetails || (localStorage.getItem('hideMainColumn') === 'true' ? true : false)
  );

  const toggleMainColumn = () => {
    const state = !hideMainColumn;

    setHideMainColumn(state);
    setHistory({ hidedetails: !hideMainColumn });

    // Persist state in local storage
    localStorage.setItem('hideMainColumn', state.toString());
  };

  const goBack = e => {
    // Prevent click event to fire on parent element
    e.stopPropagation();

    // Go back to previous page in browser history
    navigate(-1);
  };

  const startResizing = useCallback(e => {
    isResizingRef.current = true;
    startXRef.current = e.clientX;
    setIsResizing(true);
  }, []);

  const stopResizing = useCallback(() => {
    if (isResizingRef.current) {
      isResizingRef.current = false;
      setIsResizing(false);
      // Persist on drag end only — avoids synchronous I/O on every pixel change
      // Sync rawWidthRef so the next drag session starts from the displayed width
      setMainColumnWidth(prev => {
        rawWidthRef.current = prev;
        localStorage.setItem('mainColumnWidth', prev);
        return prev;
      });
    }
  }, []);

  const handleMouseMove = useCallback(e => {
    if (!isResizingRef.current) return;
    const deltaX = e.clientX - startXRef.current;
    // Accumulate movement in ref so small increments can escape the snap zone
    rawWidthRef.current = Math.max(rawWidthRef.current + deltaX, 200);
    const displayWidth =
      Math.abs(rawWidthRef.current - initialWidth) <= 10 ? initialWidth : rawWidthRef.current;
    setMainColumnWidth(displayWidth);
    startXRef.current = e.clientX;
  }, []); // no deps — reads from refs, never recreated during drag

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', stopResizing);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, []); // registered once on mount, never torn down mid-drag

  return (
    <div
      className={
        'MainColumn column' + (hideMainColumn ? ' hide' : '') + (isResizing ? ' is-resizing' : '')
      }
      onClick={() => {
        if (hideMainColumn) {
          toggleMainColumn();
        }
      }}
      style={{
        flexBasis: `${mainColumnWidth}px`,
        flexShrink: 0,
        flexGrow: 0,
      }}
    >
      <button
        className="button-back"
        onClick={e => goBack(e)}
        onMouseEnter={() => showTooltip({ title: 'Go back to previous page' })}
        onMouseLeave={hideTooltip}
      >
        <span className="button-back-arrow"></span>
        <span className="button-back-label">Back</span>
      </button>

      <div
        className="button-toggle-main-column"
        onClick={toggleMainColumn}
        onMouseEnter={() =>
          showTooltip({
            title: hideMainColumn ? 'Expand object details pane' : 'Minimize object details pane',
          })
        }
        onMouseLeave={hideTooltip}
      >
        <span></span>
      </div>

      <div className="main-column-content">{children}</div>

      <div className="main-column-resize-control" onMouseDown={startResizing} />
    </div>
  );
};
